import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../ui/alert-dialog";

export const Field = ({ label, children, testid }) => (
  <label className="block text-xs font-semibold text-slate-600" data-testid={testid}>{label}<div className="mt-1 font-normal">{children}</div></label>
);

export const SelectBox = ({ value, onChange, options, testid, className = "" }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`h-9 rounded-md border border-slate-200 bg-white px-2 text-sm ${className || "w-full"}`} data-testid={testid}>
    {options.map(([v, l]) => <option key={v ?? "_"} value={v ?? ""}>{l}</option>)}
  </select>
);

export { Input, Textarea };

// Explicit-save editor shell: nothing is written until Save is clicked; closing with unsaved changes asks first.
export function EditorDialog({ open, title, dirty, saving, onClose, onSave, children, testid = "content-editor" }) {
  useEffect(() => {
    if (!open || !dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [open, dirty]);
  const tryClose = () => { if (!dirty || window.confirm("You have unsaved changes. Discard them?")) onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) tryClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">{children}</div>
        <DialogFooter className="gap-2">
          {dirty && <span className="text-xs text-amber-700 self-center mr-auto" data-testid="editor-unsaved">Unsaved changes</span>}
          <Button variant="outline" onClick={tryClose} data-testid="editor-cancel">Cancel</Button>
          <Button onClick={onSave} disabled={saving || !dirty} data-testid="editor-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmDelete({ item, label, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <AlertDialog open={!!item} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent data-testid="confirm-delete">
        <AlertDialogHeader>
          <AlertDialogTitle>Permanently delete this {label}?</AlertDialogTitle>
          <AlertDialogDescription>This removes it for all users. Prefer "Inactive" if you may want it back later.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-red-600 hover:bg-red-700" disabled={busy} data-testid="confirm-delete-confirm"
            onClick={async (e) => { e.preventDefault(); setBusy(true); try { await onConfirm(); toast.success(`${label[0].toUpperCase()}${label.slice(1)} deleted`); } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); } finally { setBusy(false); } }}>
            Delete permanently
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export const ActiveBadge = ({ active }) => (
  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${active ? "bg-emerald-50 text-emerald-700" : "bg-slate-200 text-slate-600"}`} data-testid="active-badge">{active ? "Active" : "Inactive"}</span>
);

export const errText = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : (d?.[0]?.msg || "Request failed"); };
